import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CreateExemptionInput, VisitExemption } from "@msc/shared";
import { ApiError, api } from "../lib/api.js";
import { visitKeys } from "./useVisits.js";

export type ExemptionDecision = "approve" | "reject" | "withdraw" | "revoke";

export const exemptionKeys = {
  all: ["visitExemptions"] as const,
  inbox: () => [...exemptionKeys.all, "inbox"] as const,
  history: (customerId: string, segmentId: string) =>
    [...exemptionKeys.all, "history", customerId, segmentId] as const,
};

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: visitKeys.all });
    void queryClient.invalidateQueries({ queryKey: exemptionKeys.all });
  };
}

export function useRequestExemption() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: CreateExemptionInput) =>
      api.post<VisitExemption>("/visits/exemptions", input),
    onSuccess: invalidate,
  });
}

export interface DecideExemptionVars {
  id: string;
  /** Leer oder nur Leerzeichen → wird weggelassen (das Backend lehnt "" ab). */
  note?: string;
}

export function useDecideExemption(kind: ExemptionDecision) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, note }: DecideExemptionVars) => {
      const path = `/visits/exemptions/${id}/${kind}`;
      if (kind === "withdraw") return api.post<VisitExemption>(path);
      const trimmed = note?.trim();
      return api.post<VisitExemption>(path, trimmed ? { note: trimmed } : {});
    },
    onSuccess: invalidate,
  });
}

export function useExemptionInbox() {
  return useQuery<VisitExemption[]>({
    queryKey: exemptionKeys.inbox(),
    queryFn: async () =>
      (await api.get<{ items: VisitExemption[] }>("/visits/exemptions/inbox")).items,
  });
}

/** Verlauf einer Zelle, neueste zuerst. 403 = darf die Zelle nicht lesen → kein Verlauf. */
export function useExemptionHistory(customerId: string, segmentId: string, enabled = true) {
  return useQuery<VisitExemption[]>({
    queryKey: exemptionKeys.history(customerId, segmentId),
    enabled: enabled && Boolean(customerId) && Boolean(segmentId),
    retry: false,
    queryFn: async () => {
      const qs = new URLSearchParams({ customerId, segmentId }).toString();
      try {
        return (await api.get<{ items: VisitExemption[] }>(`/visits/exemptions?${qs}`)).items;
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) return [];
        throw err;
      }
    },
  });
}
